import { WorkflowGraphNode, WorkflowGraph } from 'cio/utils/visual-workflow-builder/workflow-graph';
import { isEmpty, isNone } from '@ember/utils';
import { setProperties } from '@ember/object';
import CampaignModel from 'cio/models/campaign';
import isMultiLangBranchChild from '../is-multi-lang-branch-child';

export const LAYER_HEIGHT = 20;
export const PRECONDITIONS_HEIGHT = 38;
export const IS_DELETING_HEIGHT = 20;
export const METRICS_PANEL_HEIGHT = 61;
export const METRICS_PANEL_ADDITIONAL_LINK_HEIGHT = 27;
export const NODE_SEP = 24;
export const TRIGGER_FILTERS_HEIGHT = 21;

export const DEFAULT_SIZE = Object.freeze({
  width: 300,
  height: 68,
});

export const DEFAULT_SIZE_SHORT = Object.freeze({
  width: DEFAULT_SIZE.width,
  height: 40,
});

export const NODE_SIZES: Record<string, { width: number; height: number }> = Object.freeze({
  collection_query_action: Object.freeze({
    width: DEFAULT_SIZE.width,
    height: 64,
  }),
  delay_seconds_action: Object.freeze({
    width: DEFAULT_SIZE.width,
    height: 64,
  }),
  conditional_wait_action: Object.freeze({
    width: DEFAULT_SIZE.width,
    height: 64,
  }),
  split_randomized_action: Object.freeze({
    width: 626,
    height: 143,
  }),
  // An exit action is around 40px tall, but we add extra height to leave more
  // space after an exit. This way a branch with exits will have its reconnect
  // paths a bit more balanced (creates more visually pleasing layouts)
  exit_action: Object.freeze({
    width: 70,
    height: 60,
  }), // we artificially inflate this height to create more space after an exit
  // a path label is about 23px in height, but we use the
  // taller DEFAUT_SIZE_SHORT to add extra space at the top of the label, to
  // make space for the path lines
  virtual_path_label: Object.freeze({
    // the max-width of the .vwb-branch-label class
    width: 250,
    height: DEFAULT_SIZE_SHORT.height,
  }),
  virtual_drop_area: Object.freeze({
    width: DEFAULT_SIZE.width,
    height: 30,
  }),
  virtual_rejoin: Object.freeze({
    width: 50,
    height: 10,
  }),
  virtual_dummy: Object.freeze({
    width: 50,
    height: LAYER_HEIGHT,
  }),
});

export const NODE_SIZE_VARIATIONS: Record<string, Record<string, { width: number; height: number }>> = Object.freeze({
  trigger: Object.freeze({
    api_triggered: Object.freeze({
      width: 92,
      height: DEFAULT_SIZE_SHORT.height,
    }),
    one_segment_condition: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 77,
    }),
  }),
  static_seg_update_action: Object.freeze({
    incomplete: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 97,
    }),
  }),
  multi_split_branch_action: Object.freeze({
    name_and_warnings: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 97,
    }),
  }),
  conditional_branch_action: Object.freeze({
    no_conditions_and_warnings: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 97,
    }),
    name_conditions_and_warnings: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 97,
    }),
  }),
  virtual_drop_area: Object.freeze({
    empty_campaign: Object.freeze({
      width: 600,
      height: 350,
    }),
  }),
  create_event_action: Object.freeze({
    event_name_and_warnings: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 100,
    }),
  }),
  batch_update_action: Object.freeze({
    description_and_warnings: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 100,
    }),
  }),
  conditional_wait_action: Object.freeze({
    warnings: Object.freeze({
      width: DEFAULT_SIZE.width,
      height: 93,
    }),
  }),
});

// Path labels receive a custom size when metrics mode is enabled. All other
// nodes that support displaying metrics simply get taller by the same amount,
// which is the height of the metrics panel
export const METRICS_MODE_NODE_SIZES: Record<string, { width: number; height: number }> = Object.freeze({
  virtual_path_label: Object.freeze({
    width: DEFAULT_SIZE.width,
    height: 125,
  }), // we artificially inflate this height to create space before a label
});

export function estimateSize(node: WorkflowGraphNode, currentTab: string, campaign: CampaignModel) {
  let size: { width: number; height: number } = { width: 0, height: 0 };

  if (Object.keys(NODE_SIZES).includes(node.type)) {
    size = { ...NODE_SIZES[node.type] };
  } else {
    size = { ...DEFAULT_SIZE };
  }

  if (node.type === 'virtual_path_label' && currentTab === 'metrics') {
    size = { ...METRICS_MODE_NODE_SIZES.virtual_path_label };
  }

  if (
    node.type === 'virtual_drop_area' &&
    (node.source.type === 'virtual_enter' || node.source.type === 'filter_match_delay_action') &&
    node.target.type === 'exit_action'
  ) {
    size = { ...NODE_SIZE_VARIATIONS.virtual_drop_area.empty_campaign };
  }

  if (node.type === 'virtual_enter' || node.type === 'filter_match_delay_action') {
    if (campaign.isApiTriggered) {
      size = { ...NODE_SIZE_VARIATIONS.trigger.api_triggered };
    }

    if (campaign.isSegmentTriggered) {
      if (campaign.anchors.length === 1) {
        size = { ...NODE_SIZE_VARIATIONS.trigger.one_segment_condition };
      }
    }

    if (campaign.hasFilters || (campaign.isEventTriggered && campaign.attributeFilters != null)) {
      size = { ...size, height: size.height + TRIGGER_FILTERS_HEIGHT };
    }
  }

  if (node.typeCategory === 'action') {
    let { action } = node;
    if ((action.isMultiSplitBranch || action.isRandomCohortBranch) && isEmpty(action.name)) {
      if (isEmpty(action.warnings)) {
        size = { ...DEFAULT_SIZE_SHORT };
      } else {
        size = { ...DEFAULT_SIZE };
      }
    }

    if (action.isMultiSplitBranch && !isEmpty(action.name) && !isEmpty(action.warnings)) {
      size = { ...NODE_SIZE_VARIATIONS.multi_split_branch_action.name_and_warnings };
    }

    if (action.isConditionalBranch && isEmpty(action.conditions) && !isEmpty(action.warnings)) {
      size = { ...NODE_SIZE_VARIATIONS.conditional_branch_action.no_conditions_and_warnings };
    }

    if (
      action.isConditionalBranch &&
      !isEmpty(action.conditions) &&
      isEmpty(action.name) &&
      !isEmpty(action.warnings)
    ) {
      size = { ...DEFAULT_SIZE };
    }

    if (action.isConditionalBranch && !isEmpty(action.conditions) && isEmpty(action.name) && isEmpty(action.warnings)) {
      size = { ...DEFAULT_SIZE_SHORT };
    }

    if (
      action.isConditionalBranch &&
      !isEmpty(action.conditions) &&
      !isEmpty(action.name) &&
      !isEmpty(action.warnings)
    ) {
      size = { ...NODE_SIZE_VARIATIONS.conditional_branch_action.name_conditions_and_warnings };
    }

    if (action.isManualSegmentUpdateAction && !isEmpty(action.name) && isNone(action.belongsTo('segment').id())) {
      size = { ...NODE_SIZE_VARIATIONS.static_seg_update_action.incomplete };
    }

    if ((action.type === 'push_action' || action.type === 'twilio_action') && !action.hasNoContent) {
      size = { ...DEFAULT_SIZE };
    }

    if (action.type === 'delay_time_window_action') {
      // Time window descriptions are one line tall only when they're "any day
      // at any time"
      if (action.anyDay && action.anyTime) {
        size.height = 64;
      } else {
        // In all other situations, limit the height to two lines. We'll
        // truncate long descriptions with an ellipsis and add a tooltip when
        // necessary
        size.height = 85;
      }
    }

    if (action.type === 'conditional_wait_action' && !isEmpty(action.warnings)) {
      size = { ...NODE_SIZE_VARIATIONS.conditional_wait_action.warnings };
    }

    if (action.isCreateEventAction && !isEmpty(action.eventName) && !isEmpty(action.warnings)) {
      size = { ...NODE_SIZE_VARIATIONS.create_event_action.event_name_and_warnings };
    }

    if (
      action.isBatchUpdateAction &&
      !isEmpty(action.warnings) &&
      (!isEmpty(action.eventName) || !isEmpty(action.attributes))
    ) {
      size = { ...NODE_SIZE_VARIATIONS.batch_update_action.description_and_warnings };
    }

    if (!action.isSplitTest && action.preconditions.length > 0) {
      size.height += PRECONDITIONS_HEIGHT;
    }

    if ((!action.isDelay && (action.isDeleting || action.isEvaluating)) || (action.isDelay && !action.isRunning)) {
      size.height += IS_DELETING_HEIGHT;
    }

    if (
      (action.isMessagingType || action.isSplitTest) &&
      !action.isAttributeAction &&
      !action.isManualSegmentUpdateAction &&
      currentTab === 'metrics'
    ) {
      size.height += METRICS_PANEL_HEIGHT;

      if (isMultiLangBranchChild(action)) {
        size.height += METRICS_PANEL_ADDITIONAL_LINK_HEIGHT;
      }
    }
  }

  return size;
}

export function assignSizes(workflowGraph: WorkflowGraph, currentTab: string) {
  Object.values(workflowGraph.nodes).forEach((node) => {
    let size = estimateSize(node, currentTab, workflowGraph.campaign);
    setProperties(node, {
      width: size.width,
      height: size.height,
      x: 0,
      y: 0,
    });
  });
}
